// https://getbootstrap.com/docs/5.0/components/buttons/

import { is } from '@common/is/is'
import DOM from '@common/DOM/DOM'

import type { TIconType } from '@common/components/SVGIcon/SVGIcon'
import SVGIcon from '@common/components/SVGIcon/SVGIcon'

import type { TData, TState, IChildrenProps } from '../definitions'

import './Button.css'

interface IBadge {
  readonly value: string | number
  readonly state?: TState
}

interface IProps extends IChildrenProps {
  readonly type?: TState
  readonly hasOutline?: boolean
  readonly isDisabled?: boolean
  readonly onClick?: (e: Event) => void
  readonly onceClick?: (e: Event) => void
  readonly title?: string
  readonly icon?: TIconType
  readonly href?: string
  readonly size?: 'lg' | 'md' | 'sm'
  readonly data?: TData
  readonly isSubmit?: boolean
  readonly badge?: IBadge
  readonly isRelative?: boolean
}

/**
 * Bootstrap-alapú button komponens.
 * @param props - JSX props {@link IProps}.
 */
export default function Button (props: IProps): HTMLButtonElement | HTMLAnchorElement {
  const parts = [
    'btn',
    props.hasOutline ? 'outline-' : null,
    props.type ?? 'primary'
  ].filter((p) => is.string(p))

  const spanClass = is.array(props.children) && !is.empty(props.children) ? 'me-1' : ''

  const isButton = !props.href
  const Element = isButton ? 'button' : 'a'

  const cn: readonly string[] = [
    'btn',
    props.isRelative ? 'position-relative' : '',
    parts.join('-'),
    props.size ? `btn-${ props.size }` : ''
  ]

  const btn: HTMLButtonElement | HTMLAnchorElement = (
    <Element
      className={ cn }
      { ...props.data ? { data: props.data } : {} }
    >
      {
        props.icon
          ? (
            <span className={ spanClass }>
              <SVGIcon size="18" type={ props.icon } />
            </span>
          )
          : null
      }

      { props.children }
    </Element>
  )

  if (isButton) {
    btn.type = props.isSubmit ? 'submit' : 'button'
  }
  else {
    btn.setAttribute('role', 'button');
    (btn as HTMLAnchorElement).href = props.href
  }

  if (props.isDisabled) {
    btn.setAttribute('disabled', '')
  }

  if (props.onClick) {
    DOM.on(btn, 'click', props.onClick)
  }

  if (props.onceClick) {
    DOM.once(btn, 'click', props.onceClick)
  }

  if (props.title) {
    btn.title = props.title
  }

  if (props.badge) {
    btn.classList.add('position-relative')

    const badgeClasses: readonly string[] = [
      'position-absolute', 'top-0', 'start-100',
      'translate-middle', 'badge', 'rounded-pill',
      `bg-${ props.badge.state }`
    ]

    btn.appendChild(
      <span className={ badgeClasses }>
        { props.badge.value }
      </span>
    )
  }

  return btn
}
